import { FormEvent, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { getApiErrorMessage } from '../lib/errors';
import { Field } from '../components/ui/Field';
import { Skeleton } from '../components/ui/Primitives';
import { SkeletonState } from '../components/ui/SkeletonState';
import { AuthShell } from './LoginPage';
import {
  usePasswordReset,
  usePasswordResetValidation,
  useRequestPasswordReset,
} from '../features/auth/useAuth';

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token');
  const validation = usePasswordResetValidation(token);
  const reset = usePasswordReset();
  const requestReset = useRequestPasswordReset();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const passwordsDiffer = Boolean(passwordConfirm) && password !== passwordConfirm;

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!token || password !== passwordConfirm) return;
    reset.mutate(
      { token, password },
      { onSuccess: () => navigate('/login', { replace: true, state: { passwordReset: true } }) },
    );
  };

  const onRequest = (event: FormEvent) => {
    event.preventDefault();
    requestReset.mutate({ email });
  };

  return (
    <AuthShell title="Restablecer contraseña" subtitle="Elige una nueva contraseña para tu cuenta">
      {!token && (
        <form onSubmit={onRequest} className="space-y-4">
          <p className="text-sm leading-6 text-slate-600">
            Escribe tu correo y, si existe una cuenta verificada, enviaremos un enlace de un solo
            uso.
          </p>
          <Field
            id="reset-email"
            label="Correo"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          {requestReset.isSuccess && (
            <p className="text-sm text-income">
              Si el correo puede recibir el enlace, ya lo enviamos.
            </p>
          )}
          {requestReset.isError && (
            <p role="alert" className="text-sm text-expense">
              {getApiErrorMessage(requestReset.error)}
            </p>
          )}
          <button
            type="submit"
            disabled={requestReset.isPending}
            className="tap-target w-full rounded-lg bg-slate-900 px-4 py-2.5 text-base font-semibold text-white disabled:opacity-60"
          >
            {requestReset.isPending ? 'Enviando…' : 'Enviar enlace'}
          </button>
        </form>
      )}
      {token && validation.isPending && (
        <SkeletonState label="Validando enlace…" className="space-y-3">
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-11 w-full" />
        </SkeletonState>
      )}
      {token && validation.isError && (
        <ResetError
          message={getApiErrorMessage(
            validation.error,
            'Este enlace no es válido, ya fue usado o venció.',
          )}
        />
      )}
      {validation.data && (
        <form onSubmit={onSubmit} className="space-y-4">
          <Field
            id="new-password"
            label="Nueva contraseña"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            helperText="Usa al menos 8 caracteres."
          />
          <Field
            id="new-password-confirm"
            label="Confirmar nueva contraseña"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
            errorText={passwordsDiffer ? 'Las contraseñas no coinciden.' : undefined}
          />
          {reset.isError && (
            <p role="alert" className="text-sm text-expense">
              {getApiErrorMessage(reset.error)}
            </p>
          )}
          <button
            type="submit"
            disabled={reset.isPending}
            className="tap-target w-full rounded-lg bg-slate-900 px-4 py-2.5 text-base font-semibold text-white disabled:opacity-60"
          >
            {reset.isPending ? 'Guardando…' : 'Guardar nueva contraseña'}
          </button>
        </form>
      )}
    </AuthShell>
  );
}

function ResetError({ message }: { message: string }) {
  return (
    <div className="space-y-3 text-center">
      <p role="alert" className="text-sm leading-6 text-expense">
        {message}
      </p>
      <Link
        to="/login"
        className="tap-target inline-flex items-center text-sm font-semibold text-slate-900 underline"
      >
        Ir a iniciar sesión
      </Link>
    </div>
  );
}
